import { useState, useRef, useEffect, useMemo } from "react";
import type { EventPublic, VenuePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { notifySaving, notifySaved } from "@/lib/save-status";
import { directionsUrl } from "@/lib/directions";
import { Input } from "@/components/ui/input";
import { X, MapPin, Navigation, Star } from "lucide-react";

interface VenuesManagerProps {
  event: EventPublic;
  canEdit: boolean;
  setEvent: React.Dispatch<React.SetStateAction<EventPublic | null>>;
}

const VENUE_PILL = {
  bg: "bg-amber-100",
  border: "border-amber-300",
  text: "text-amber-800",
  primaryBg: "bg-amber-200",
};

export function VenuesManager({ event, canEdit, setEvent }: VenuesManagerProps) {
  const [search, setSearch] = useState("");
  const [showDrop, setShowDrop] = useState(false);
  const [standaloneVenues, setStandaloneVenues] = useState<VenuePublic[]>([]);
  const [loadedVenues, setLoadedVenues] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [fieldInputs, setFieldInputs] = useState<Record<string, string>>({});

  // 127 R120 -- when an event has 2+ venues, scope the per-venue field rows
  // to one venue at a time so each venue's heading stays attached to its own
  // field list. Default tab = "All" preserves the prior stacked behaviour.
  const ALL_VENUES = "__all__";
  const [activeVenueTab, setActiveVenueTab] = useState<string>(ALL_VENUES);

  // 123 R158 -- court add/delete affordances are gated by the server-issued
  // `manage_courts` action. Parents and other non-write roles never see the
  // X/Add buttons even when the broader event-edit affordances are present.
  const canManageCourts = event._actions.includes("manage_courts");

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (showDrop && !loadedVenues) {
      api.listVenues().then((r) => { setStandaloneVenues(r.venues); setLoadedVenues(true); }).catch(() => {});
    }
  }, [showDrop, loadedVenues]);

  const existingSchoolIds = useMemo(
    () => new Set(event.venues.filter((v) => v.school_id).map((v) => v.school_id)),
    [event.venues],
  );
  const existingVenueIds = useMemo(
    () => new Set(event.venues.filter((v) => v.venue_id).map((v) => v.venue_id)),
    [event.venues],
  );

  const q = search.toLowerCase();

  const schoolCandidates = useMemo(() => {
    return event.schools
      .filter((s) => !existingSchoolIds.has(s.school_id))
      .filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [event.schools, existingSchoolIds, q]);

  const venueCandidates = useMemo(() => {
    return standaloneVenues
      .filter((v) => !existingVenueIds.has(v.id))
      .filter((v) => !q || v.name.toLowerCase().includes(q));
  }, [standaloneVenues, existingVenueIds, q]);

  const canAddAdhoc = useMemo(() => {
    if (!q) return false;
    const existingNames = new Set(event.venues.map((v) => v.display_name.toLowerCase()));
    if (existingNames.has(q)) return false;
    const hasExactSchool = event.schools.some((s) => s.name.toLowerCase() === q);
    const hasExactVenue = standaloneVenues.some((v) => v.name.toLowerCase() === q);
    return !hasExactSchool && !hasExactVenue;
  }, [q, event.venues, event.schools, standaloneVenues]);

  const hasResults = schoolCandidates.length > 0 || venueCandidates.length > 0;

  async function addAdhocVenue(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    setSearch(""); setShowDrop(false);
    notifySaving();
    try {
      const created = await api.createVenue({ name: trimmed });
      const res = await api.addEventVenue(event.id, { venue_id: created.venue.id, school_id: null });
      setEvent((prev) => prev ? { ...prev, venues: [...prev.venues, res.event_venue] } : prev);
      setStandaloneVenues((prev) => [...prev, created.venue]);
      notifySaved();
    } catch { /* silent */ }
  }

  async function addSchoolVenue(schoolId: string) {
    setSearch(""); setShowDrop(false);
    notifySaving();
    try {
      const res = await api.addEventVenue(event.id, { school_id: schoolId, venue_id: null });
      setEvent((prev) => prev ? { ...prev, venues: [...prev.venues, res.event_venue] } : prev);
      notifySaved();
    } catch { /* silent */ }
  }

  async function addStandaloneVenue(venueId: string) {
    setSearch(""); setShowDrop(false);
    notifySaving();
    try {
      const res = await api.addEventVenue(event.id, { venue_id: venueId, school_id: null });
      setEvent((prev) => prev ? { ...prev, venues: [...prev.venues, res.event_venue] } : prev);
      notifySaved();
    } catch { /* silent */ }
  }

  async function remove(eventVenueId: string) {
    const removed = event.venues.find((v) => v.id === eventVenueId);
    setEvent((prev) => prev ? { ...prev, venues: prev.venues.filter((v) => v.id !== eventVenueId) } : prev);
    // 127 R120 -- if the deleted venue was the active tab, fall back to "All".
    if (activeVenueTab === eventVenueId) setActiveVenueTab(ALL_VENUES);
    notifySaving();
    try {
      await api.deleteEventVenue(event.id, eventVenueId);
      notifySaved();
    } catch {
      // 123 R158-style fix: revert on failure so the user immediately sees the
      // venue reappear instead of having it silently re-materialise on refetch.
      if (removed) {
        setEvent((prev) => prev ? { ...prev, venues: [...prev.venues, removed] } : prev);
      }
    }
  }

  async function promote(eventVenueId: string) {
    notifySaving();
    try {
      await api.patchEventVenue(event.id, eventVenueId, { is_primary: true });
      setEvent((prev) => prev ? {
        ...prev,
        venues: prev.venues.map((v) =>
          v.id === eventVenueId ? { ...v, is_primary: true } : { ...v, is_primary: false },
        ),
      } : prev);
      notifySaved();
    } catch { /* silent */ }
  }

  const sorted = useMemo(
    () => [...event.venues].sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return a.display_name.localeCompare(b.display_name);
    }),
    [event.venues],
  );

  // --- Per-venue field management ---

  const courtsByVenue = useMemo(() => {
    const map = new Map<string, typeof event.courts>();
    for (const c of event.courts) {
      if (c.event_venue_id) {
        const list = map.get(c.event_venue_id) ?? [];
        list.push(c);
        map.set(c.event_venue_id, list);
      }
    }
    return map;
  }, [event.courts]);

  async function addVenueField(venueId: string, fieldName: string) {
    const trimmed = fieldName.trim();
    if (!trimmed) return;
    setFieldInputs((prev) => ({ ...prev, [venueId]: "" }));
    notifySaving();
    try {
      const res = await api.addEventCourt(event.id, { name: trimmed, event_venue_id: venueId });
      setEvent((prev) => prev ? { ...prev, courts: [...prev.courts, res.court] } : prev);
      notifySaved();
    } catch { /* silent */ }
  }

  async function removeVenueField(courtEntryId: string) {
    // 123 R158: snapshot before the optimistic removal so we can revert on
    // failure. The previous silent-catch left the UI stale until next refetch,
    // which the BA reported as "the deletion doesn't seem to persist".
    const removed = event.courts.find((c) => c.id === courtEntryId);
    setEvent((prev) => prev ? { ...prev, courts: prev.courts.filter((c) => c.id !== courtEntryId) } : prev);
    notifySaving();
    try {
      await api.removeEventCourt(event.id, courtEntryId);
      notifySaved();
    } catch {
      if (removed) {
        setEvent((prev) => prev ? { ...prev, courts: [...prev.courts, removed] } : prev);
      }
    }
  }

  return (
    <div className="min-w-0 flex-1" data-region="venues">
      <div className="mb-1.5 flex items-center gap-2">
        <h3 className="shrink-0 text-sm font-medium text-muted-foreground">Venues</h3>
        {canEdit && (
          <div ref={ref} className="relative min-w-0 flex-1 max-w-xs">
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowDrop(true); }}
              onFocus={() => setShowDrop(true)}
              placeholder="Add venue..."
              className="h-7 text-xs"
              data-field="venue-search"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (schoolCandidates.length === 1 && venueCandidates.length === 0 && !canAddAdhoc) addSchoolVenue(schoolCandidates[0].school_id);
                  else if (venueCandidates.length === 1 && schoolCandidates.length === 0 && !canAddAdhoc) addStandaloneVenue(venueCandidates[0].id);
                  else if (canAddAdhoc && schoolCandidates.length === 0 && venueCandidates.length === 0) addAdhocVenue(search);
                }
                if (e.key === "Escape") setShowDrop(false);
              }}
            />
            {showDrop && (
              <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-input bg-background shadow-md">
                {schoolCandidates.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase text-muted-foreground">Event schools</div>
                    {schoolCandidates.map((s) => (
                      <button key={s.school_id} type="button" onClick={() => addSchoolVenue(s.school_id)}
                        className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted">
                        <MapPin className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                        {s.name}
                      </button>
                    ))}
                  </>
                )}
                {venueCandidates.length > 0 && (
                  <>
                    <div className="px-3 py-1 text-[10px] font-semibold uppercase text-muted-foreground">Standalone venues</div>
                    {venueCandidates.map((v) => (
                      <button key={v.id} type="button" onClick={() => addStandaloneVenue(v.id)}
                        className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted">
                        <MapPin className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                        {v.name}
                        {v.location && <span className="ml-2 text-xs text-muted-foreground">{v.location}</span>}
                      </button>
                    ))}
                  </>
                )}
                {search.trim() && canAddAdhoc && (
                  <button type="button" onClick={() => addAdhocVenue(search)}
                    className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted" data-action="add-adhoc-venue">
                    <span className="text-muted-foreground">Create venue:</span>
                    <span className="ml-1 font-medium">{search.trim()}</span>
                  </button>
                )}
                {!hasResults && !canAddAdhoc && search.trim() && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Already added</div>
                )}
                {!hasResults && !search.trim() && (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Type a name to add a school or create a venue</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((v) => (
          <span
            key={v.id}
            data-item="venue"
            data-item-id={v.id}
            className={`group/chip inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium whitespace-nowrap ${VENUE_PILL.bg} ${VENUE_PILL.border} ${VENUE_PILL.text}`}
          >
            {v.is_primary ? (
              <span title="Primary venue"><Star className="h-3 w-3 shrink-0 fill-current" /></span>
            ) : canEdit ? (
              <button type="button" title="Set as primary" onClick={() => promote(v.id)} data-action="set-primary"
                className="hidden shrink-0 opacity-50 hover:opacity-100 group-hover/chip:inline-flex">
                <Star className="h-3 w-3" />
              </button>
            ) : null}
            {v.display_name}
            {v.latitude != null && (
              <a
                href={directionsUrl(v)}
                target="_blank"
                rel="noopener noreferrer"
                title="Get directions"
                className="shrink-0 opacity-60 hover:opacity-100"
                onClick={(e) => e.stopPropagation()}
              >
                <Navigation className="h-3 w-3" />
              </a>
            )}
            {canEdit && (
              <button type="button" title={`Remove ${v.display_name}`} onClick={() => remove(v.id)} data-action="remove-venue"
                className="hidden hover:text-destructive group-hover/chip:inline-flex">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {event.venues.length === 0 && (
          <span className="text-xs text-muted-foreground">No venues -- host school used for directions</span>
        )}
      </div>

      {/* Per-venue fields -- add courts/fields scoped to each venue.
          123 R158: gated on the server-issued `manage_courts` action, not on
          the broader `canEdit`, so parent/super_parent roles never see the
          per-venue add input or delete X.
          127 R120: with 2+ venues, a tab strip scopes the field list to one
          venue at a time so each venue's heading stays attached to its own
          fields. "All" preserves the prior stacked layout. */}
      {canManageCourts && sorted.length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {sorted.length >= 2 && (
            <div className="flex flex-wrap items-center gap-1" data-region="venue-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeVenueTab === ALL_VENUES ? "true" : "false"}
                data-venue-tab="all"
                onClick={() => setActiveVenueTab(ALL_VENUES)}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  activeVenueTab === ALL_VENUES
                    ? "border-amber-300 bg-amber-100 text-amber-900"
                    : "border-transparent text-muted-foreground hover:bg-muted"
                }`}
              >
                All
              </button>
              {sorted.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  aria-selected={activeVenueTab === v.id ? "true" : "false"}
                  data-venue-tab={v.id}
                  onClick={() => setActiveVenueTab(v.id)}
                  className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                    activeVenueTab === v.id
                      ? "border-amber-300 bg-amber-100 text-amber-900"
                      : "border-transparent text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {v.display_name}
                </button>
              ))}
            </div>
          )}
          {sorted
            .filter((v) => activeVenueTab === ALL_VENUES || v.id === activeVenueTab)
            .map((v) => {
              const fields = courtsByVenue.get(v.id) ?? [];
              const inputVal = fieldInputs[v.id] ?? "";
              return (
                <div key={v.id} className="flex items-center gap-1.5 flex-wrap" data-venue-fields={v.id}>
                  <span className="text-[11px] font-medium text-muted-foreground shrink-0 w-[80px] truncate" title={v.display_name}>
                    {v.display_name}:
                  </span>
                  {fields.map((f) => (
                    <span key={f.id} className="group/field inline-flex items-center gap-0.5 rounded border border-dashed border-amber-300/60 bg-amber-50 px-2 py-0.5 text-[11px] text-amber-900">
                      {f.name}
                      <button type="button" onClick={() => removeVenueField(f.id)}
                        className="hidden opacity-50 hover:opacity-100 hover:text-destructive group-hover/field:inline-flex" title={`Remove ${f.name}`}>
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                  <Input
                    value={inputVal}
                    onChange={(e) => setFieldInputs((prev) => ({ ...prev, [v.id]: e.target.value }))}
                    placeholder="Add field..."
                    className="h-5 w-24 text-[11px] px-1.5"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addVenueField(v.id, inputVal);
                      }
                    }}
                  />
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}
